import { useState, useCallback, useRef, useEffect } from 'react';
import type { Application } from 'pixi.js';
import { BEST_SCORE_STORAGE_KEY } from '@/game/configs';
interface UseGameStateReturn {
  score: number;
  bestScore: number;
  gameStarted: boolean;
  sessionStartedAt: number | null;
  appRef: React.MutableRefObject<Application | null>;
  startGame: () => void;
  incrementScore: () => void;
  stopGame: () => void;
  resetGame: () => void;
}
function loadBestScore(): number {
  try {
    const stored = localStorage.getItem(BEST_SCORE_STORAGE_KEY);
    if (stored !== null) {
      const parsed = JSON.parse(stored);
      if (typeof parsed === 'number' && Number.isFinite(parsed) && parsed >= 0) {
        return Math.floor(parsed);
      }
    }
  } catch {
    // Swallow errors for private mode / disabled storage
  }
  return 0;
}
function saveBestScore(score: number): void {
  try {
    localStorage.setItem(BEST_SCORE_STORAGE_KEY, JSON.stringify(score));
  } catch {
    // Swallow errors for private mode / disabled storage
  }
}
export function useGameState(): UseGameStateReturn {
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(loadBestScore);
  const [gameStarted, setGameStarted] = useState(false);
  const [sessionStartedAt, setSessionStartedAt] = useState<number | null>(null);
  const appRef = useRef<Application | null>(null);
  const sessionStartRef = useRef<number | null>(null);
  const startGame = useCallback(() => {
    setGameStarted(true);
    setScore(0);
    const now = Date.now();
    setSessionStartedAt(now);
    sessionStartRef.current = now;
  }, []);
  const incrementScore = useCallback(() => {
    setScore((prev) => {
      const newScore = prev + 1;
      setBestScore((currentBest) => {
        if (newScore > currentBest) {
          saveBestScore(newScore);
          return newScore;
        }
        return currentBest;
      });
      return newScore;
    });
  }, []);
  const stopGame = useCallback(() => {
    setGameStarted(false);
    setSessionStartedAt(null);
    sessionStartRef.current = null;
  }, []);
  const resetGame = useCallback(() => {
    setScore(0);
    setGameStarted(false);
    setSessionStartedAt(null);
    sessionStartRef.current = null;
  }, []);
  // Hydrate best score on mount
  useEffect(() => {
    setBestScore(loadBestScore());
  }, []);
  return {
    score,
    bestScore,
    gameStarted,
    sessionStartedAt,
    appRef,
    startGame,
    incrementScore,
    stopGame,
    resetGame,
  };
}